/**
 * ActionItemsDrawer — persistent right-side drawer of recommendations.
 *
 * Surfaces what the recommendation engine flagged. Each item has:
 *   - Severity (critical / warning / info / positive)
 *   - Category icon
 *   - Title + body
 *   - "Drill in" link (deep-link to the underlying data)
 *   - Optional one-click action button
 *   - Dismiss button (for non-critical items)
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle, Info, CheckCircle2, XCircle, X, ArrowRight, Bell,
  ShieldAlert, Users, Target, DollarSign, Cpu, BookOpen, Bot,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { ActionItem, ActionItemCategory, ActionItemSeverity } from '@/lib/mock-insights-data';

const CATEGORY_ICONS: Record<ActionItemCategory, LucideIcon> = {
  safety:     ShieldAlert,
  adoption:   Users,
  quality:    Target,
  cost:       DollarSign,
  capacity:   Cpu,
  knowledge:  BookOpen,
  agents:     Bot,
};

const SEVERITY_STYLES: Record<ActionItemSeverity, {
  border: string; bg: string; iconBg: string; icon: LucideIcon; label: string; labelBg: string;
}> = {
  critical: {
    border: 'border-l-red-500',
    bg: 'bg-red-50/40 dark:bg-red-950/20',
    iconBg: 'bg-red-100 dark:bg-red-950/60 text-red-700 dark:text-red-300',
    icon: XCircle,
    label: 'Critical',
    labelBg: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
  },
  warning: {
    border: 'border-l-amber-500',
    bg: 'bg-amber-50/40 dark:bg-amber-950/20',
    iconBg: 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300',
    icon: AlertTriangle,
    label: 'Warning',
    labelBg: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  },
  info: {
    border: 'border-l-blue-500',
    bg: 'bg-blue-50/40 dark:bg-blue-950/20',
    iconBg: 'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300',
    icon: Info,
    label: 'Info',
    labelBg: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
  },
  positive: {
    border: 'border-l-emerald-500',
    bg: 'bg-emerald-50/40 dark:bg-emerald-950/20',
    iconBg: 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300',
    icon: CheckCircle2,
    label: 'Good',
    labelBg: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  },
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export interface ActionItemsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: ActionItem[];
}

export function ActionItemsDrawer({ open, onOpenChange, items }: ActionItemsDrawerProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const visible = items.filter(i => !dismissed.has(i.id));

  // Sort: critical first, then warning, then info, then positive; within each, newest first
  const order: Record<ActionItemSeverity, number> = { critical: 0, warning: 1, info: 2, positive: 3 };
  const sorted = [...visible].sort((a, b) => {
    const sevDiff = order[a.severity] - order[b.severity];
    if (sevDiff !== 0) return sevDiff;
    return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime();
  });

  const grouped = {
    critical: sorted.filter(i => i.severity === 'critical'),
    warning:  sorted.filter(i => i.severity === 'warning'),
    info:     sorted.filter(i => i.severity === 'info'),
    positive: sorted.filter(i => i.severity === 'positive'),
  };

  const handleDismiss = (id: string) => {
    setDismissed(prev => new Set(prev).add(id));
    toast.success('Action item dismissed');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md w-full overflow-hidden flex flex-col p-0">
        <SheetHeader className="px-5 pt-5 pb-3 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            Action Items
            <Badge variant="secondary" className="text-[10px] tabular-nums">{visible.length}</Badge>
          </SheetTitle>
          <SheetDescription className="text-xs">
            What to act on today, in priority order. The recommendation engine surfaces these automatically.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-4 space-y-5">
            {grouped.critical.length > 0 && (
              <Group label="Critical" items={grouped.critical} onDismiss={handleDismiss} />
            )}
            {grouped.warning.length > 0 && (
              <Group label="Warnings" items={grouped.warning} onDismiss={handleDismiss} />
            )}
            {grouped.info.length > 0 && (
              <Group label="Information" items={grouped.info} onDismiss={handleDismiss} />
            )}
            {grouped.positive.length > 0 && (
              <Group label="Wins" items={grouped.positive} onDismiss={handleDismiss} />
            )}

            {visible.length === 0 && (
              // #614 — the previous "All clear / operating within normal
              // parameters" copy claimed a healthy verdict that the
              // recommendation engine did not in fact evaluate. The
              // backing API is still being wired, so an empty list
              // means "no items surfaced" — not "everything is fine".
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <CheckCircle2 className="h-8 w-8 text-muted-foreground/40 mb-2" />
                <p className="text-sm font-medium">No items to show</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                  The recommendation engine has not surfaced any action items for the
                  current filter. This does not on its own mean the platform is
                  operating normally — connect a recommendation source to enable
                  automated guidance.
                </p>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Group({
  label, items, onDismiss,
}: {
  label: string;
  items: ActionItem[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2 px-1">
        {label} <span className="tabular-nums">({items.length})</span>
      </p>
      <div className="space-y-2">
        {items.map(item => (
          <ActionItemRow key={item.id} item={item} onDismiss={onDismiss} />
        ))}
      </div>
    </div>
  );
}

function ActionItemRow({ item, onDismiss }: { item: ActionItem; onDismiss: (id: string) => void }) {
  const sev = SEVERITY_STYLES[item.severity];
  const SevIcon = sev.icon;
  const CatIcon = CATEGORY_ICONS[item.category];

  return (
    <div className={cn(
      'rounded-md border-l-4 border border-border p-3 space-y-2',
      sev.border, sev.bg,
    )}>
      <div className="flex items-start gap-2">
        <div className={cn('h-7 w-7 rounded-md flex items-center justify-center shrink-0', sev.iconBg)}>
          <SevIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <Badge variant="outline" className={cn('text-[9px] px-1 py-0 h-4', sev.labelBg)}>
              {sev.label}
            </Badge>
            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 capitalize gap-1">
              <CatIcon className="h-2.5 w-2.5" />
              {item.category}
            </Badge>
            <span className="text-[10px] text-muted-foreground tabular-nums">{relativeTime(item.detectedAt)}</span>
          </div>
          <p className="text-xs font-semibold leading-snug">{item.title}</p>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">{item.body}</p>
        </div>
      </div>

      {(item.drillDownUrl || item.oneClickAction || item.dismissible) && (
        <>
          <Separator />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              {item.oneClickAction && (
                <Button
                  size="sm"
                  variant="default"
                  className="h-6 text-[11px] gap-1"
                  onClick={() => {
                    toast.success(item.oneClickAction!.toastMessage);
                    onDismiss(item.id);
                  }}
                >
                  {item.oneClickAction.label}
                </Button>
              )}
              {item.drillDownUrl && (
                <Link
                  to={item.drillDownUrl as any}
                  className="h-6 text-[11px] inline-flex items-center gap-1 px-2 rounded-md hover:bg-muted transition-colors text-foreground"
                >
                  Drill in <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </div>
            {item.dismissible && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                onClick={() => onDismiss(item.id)}
                title="Dismiss"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
